import { useState } from "react";

import type { AdminTicketDecisionTreePathDto } from "@/api/types";
import { ChevronDownIcon } from "@/components/Icons/ChevronDownIcon";
import { ChevronUpIcon } from "@/components/Icons/ChevronUpIcon";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Text } from "@/design-system/Text";
import { useTranslation } from "@/translations";
import { FloatingPopover, FloatingPopoverContent, FloatingPopoverTrigger } from "@/ui-adapters/FloatingPopover";

interface TicketCategoryDecisionPathProps {
  categoryName: string;
  path: AdminTicketDecisionTreePathDto;
}

export function TicketCategoryDecisionPath({ categoryName, path }: TicketCategoryDecisionPathProps): React.ReactNode {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const location =
    path.location === "inside"
      ? t("page.ticket-category-tree.location-type.inside")
      : t("page.ticket-category-tree.location-type.outside");
  const steps = [location, ...path.nodes];
  const categoryLabel = `${t("page.tickets.details.info.category")}: ${categoryName}`;

  return (
    <FloatingPopover open={isOpen} onOpenChange={setIsOpen} placement="bottom-start" distance={9}>
      <FloatingPopoverTrigger asChild>
        <Button variant="ghostSecondary" emphasis onClick={() => setIsOpen((current) => !current)}>
          <Text variant="body-emphasis" overflow="normal">
            {categoryName}
          </Text>
          {isOpen ? <ChevronUpIcon size={16} tone="secondary" /> : <ChevronDownIcon size={16} tone="secondary" />}
        </Button>
      </FloatingPopoverTrigger>
      <FloatingPopoverContent>
        <div aria-label={categoryLabel} className="z-overlay w-max max-w-overlay-fluid outline-hidden">
          <Box padding="md" background="default" border="secondary" radius="lg" elevation="overlay">
            <ol>
              {steps.map((step, index) => {
                const isLast = index === steps.length - 1;

                return (
                  <li className="flex min-w-0 gap-space-sm" key={`${index}-${step}`}>
                    <span aria-hidden="true" className="relative flex w-icon-inline shrink-0 justify-center">
                      {index > 0 && <span className="absolute top-0 h-space-sm w-px bg-border-brand" />}
                      <span
                        className={
                          isLast
                            ? "z-raised mt-space-sm size-status-dot shrink-0 rounded-full border border-border-brand bg-background-brand"
                            : "z-raised mt-space-sm size-status-dot shrink-0 rounded-full border border-border-brand bg-background-default"
                        }
                      />
                      {!isLast && <span className="absolute top-space-md bottom-0 w-px bg-border-brand" />}
                    </span>
                    <span className={isLast ? "min-w-0" : "min-w-0 pb-space-sm"}>
                      <Text variant={isLast ? "body-emphasis" : "body"} overflow="normal">
                        {step}
                      </Text>
                    </span>
                  </li>
                );
              })}
            </ol>
          </Box>
        </div>
      </FloatingPopoverContent>
    </FloatingPopover>
  );
}
